import { useMemo, useState } from 'react';
import { Reservation, ServiceBooking, ServiceBookingPaxGroup, Person, Business, EMPTY_PERSON } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { TagIcon } from '@/lib/tag-utils';
import { IconChevronDown, IconGift, IconPhone, IconStack2, IconUser, IconUsersGroup, IconId } from '@tabler/icons-react';
import { PAYMENT_STATUS_COLORS } from '@turizi/shared';
import { ServiceTitle, ServiceTitleStatic } from './services/service-title';
import { BookingGroupSize, ShowPaxGroup } from './snippets';
import BookingServiceOrders from './service-booking/booking-service-orders';
import ServiceDatePopover from './service-date-popover';
import { useUpdateServiceBooking } from '@/hooks/api-hooks';
import SelectTimePopover from './select-time-popover';
import ProviderSelector from './service-booking/provider-selector';
import CoordinatorSelector from './service-booking/coordinator-selector';
import ShowSelfAttrs from './show-self-attrs';
import { sortServiceBookings } from '@/lib/bookings-helper';
import ReservationCardHeader from './reservation/reservation-card-header';
import { formatPrice, sortPersons } from '@/lib/format-utils';
import { Badge, Checkbox, Tooltip } from '@mantine/core';


interface BookingRowProps {
    booking: ServiceBooking;
    reservation?: Reservation;
    displayOptions: any;
    editable: boolean;
    isSelected?: boolean;
    onSelectRow?: (id: number) => void;
    paxColumnWidth?: number;
    titleColumnWidth?: number;
}

const getPaxGroupEstimatedWidth = (b: ServiceBooking) => {
    const categories: ('adults' | 'children' | 'seniors' | 'infants' | 'guests')[] = [
        'infants', 'children', 'adults', 'seniors', 'guests'
    ];

    let activeCategories = 0;
    let categoryWidthsSum = 0;
    let allocatedSum = 0;

    categories.forEach(key => {
        const count = b[key] || 0;
        if (count > 0) {
            activeCategories++;
            const countStr = String(count);
            // 16px (circle letter badge + gap) + digits * 8px
            categoryWidthsSum += 16 + (countStr.length * 8);
            if (key !== 'infants') {
                allocatedSum += count;
            }
        }
    });

    if (activeCategories === 0) {
        return 105;
    }

    const gapsWidth = (activeCategories - 1) * 8;
    const sumDigits = String(allocatedSum).length;
    const multiplierWidth = 20 + (sumDigits * 9);
    const paddingAndBorders = 24;

    const totalEstPaxWidth = categoryWidthsSum + gapsWidth + multiplierWidth + paddingAndBorders;

    return Math.max(105, totalEstPaxWidth);
};

const computeReservationColumnWidths = (bookings: ServiceBooking[], displayOptions?: any) => {
    let maxPaxWidth = 0;
    let maxTitleWidth = Infinity;

    bookings.forEach(b => {
        // 1. Dynamic Pax Width Estimation taking multi-digit counts and name/ID into account
        let estPax = getPaxGroupEstimatedWidth(b);

        if (!displayOptions?.groupByPaxs && displayOptions?.showPaxName) {
            const resToUse = b.reservation;
            const clientName = resToUse?.client_business?.name || 'no_client';
            const nameLen = clientName.length;
            const nameExtra = Math.min(140, Math.max(80, Math.round(nameLen * 6.5) + 35));
            estPax += nameExtra;
        }

        if (estPax > maxPaxWidth) {
            maxPaxWidth = estPax;
        }
        // 2. Title Width Estimation
        const service = b.service_data;
        const titleName = ServiceTitleStatic(service)

        // Create an in-memory canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set the exact font style and size (e.g., "16px Arial")
        ctx.font = "1rem Helvetica";

        // Measure and return the width in pixels
        const estTitle = ctx.measureText(titleName).width;

        if (estTitle > maxTitleWidth) {
            maxTitleWidth = estTitle;
        }
    });
    maxTitleWidth = 380;

    return { maxPaxWidth, maxTitleWidth };
};

function BookingGridRow({
    booking,
    reservation,
    displayOptions,
    editable,
    isSelected = false,
    onSelectRow,
    paxColumnWidth,
    titleColumnWidth,
}: BookingRowProps) {
    const { t } = useTranslation();
    const service = booking.service_data;

    const {
        mutate: updateMutation,
        isPending: savingDate
    } = useUpdateServiceBooking();

    const {
        mutate: updateMutationHour,
        isPending: savingHour
    } = useUpdateServiceBooking();

    const handleBookingDateChange = (value: [string | undefined, string | undefined]) => {
        updateMutation({
            id: booking.id,
            start_day: value[0],
            end_day: value[1],
        });
    };

    const handleStartHourChange = (value: string) => {
        updateMutationHour({
            id: booking.id,
            start_hour: value,
        });
    };

    const isDateMatched = useMemo(() => {
        if (!displayOptions?.highlightDate || !booking.start_day) return false;
        const targetDate = String(displayOptions.highlightDate).slice(0, 10);
        const targetDayNumber = targetDate.slice(8, 10);

        const startStr = String(booking.start_day).slice(0, 10);
        const startDayNumber = startStr.slice(8, 10);
        const endStr = booking.end_day ? String(booking.end_day).slice(0, 10) : null;

        const rangeMatch = startStr <= targetDate && (endStr ? endStr >= targetDate : true);
        const dayMatch = Boolean(targetDayNumber && startDayNumber === targetDayNumber);

        return rangeMatch || dayMatch;
    }, [booking.start_day, booking.end_day, displayOptions?.highlightDate]);

    return (
        <div className={`
            text-xs 
            flex flex-wrap items-start justify-between gap-2.5 
            px-2.5 py-1.5 
            border-b border-slate-300 last:border-b-0 
            transition-colors duration-150 
            ${isDateMatched
                ? 'bg-green-100/90 border-l-4 border-l-green-500 font-bold hover:bg-green-200/90 text-green-950 shadow-2xs'
                : 'hover:bg-indigo-50/50'
            }`}>
            {/* 1. Toggle/Action */}
            {(onSelectRow) && (
                <div className="shrink-0 text-center flex items-center justify-center">
                    {onSelectRow && (
                        <Checkbox
                            checked={!!isSelected}
                            onChange={() => onSelectRow(booking.id)}
                        />
                    )}
                </div>
            )}

            {/* Dates */}
            <div className="shrink-0 w-[70px]">
                <ServiceDatePopover
                    icon={true}
                    size="xs"
                    limits={[
                        reservation?.start_day || booking?.reservation?.start_day,
                        reservation?.end_day || booking?.reservation?.end_day
                    ]}
                    serviceCategory={service?.category}
                    value={[booking.start_day, booking.end_day]}
                    onChange={handleBookingDateChange}
                    loading={savingDate}
                    disabled={!editable}
                />
            </div>

            {/* Start Hour */}
            {displayOptions.showHour && (
                <div className="shrink-0 w-[80px]">
                    <SelectTimePopover
                        icon={true}
                        size="xs"
                        value={booking.start_hour}
                        onChange={handleStartHourChange}
                        loading={savingHour}
                        disabled={!editable}
                    />
                </div>
            )}

            {/* Passengers / Pax Size */}
            {displayOptions.groupByPaxs ? (
                <div
                    className="shrink-0 flex items-center"
                    style={{ minWidth: paxColumnWidth ? `${paxColumnWidth}px` : undefined }}
                >
                    <ShowPaxGroup
                        adults={booking.adults}
                        children={booking.children}
                        seniors={booking.seniors}
                        infants={booking.infants}
                        guests={booking.guests}
                    />
                </div>
            ) : displayOptions.showPaxs && (
                <div
                    className="shrink-0"
                    style={{ minWidth: paxColumnWidth ? `${paxColumnWidth}px` : undefined }}
                >
                    <BookingGroupSize
                        reservation={reservation}
                        booking={booking}
                        showPaxName={displayOptions.showPaxName}
                        showPhone={displayOptions.showPaxPhone}
                    />
                </div>
            )}

            {/* Service Name */}
            {!displayOptions.hideServiceTitle && service && (
                <div
                    className="shrink-0"
                    style={{ width: titleColumnWidth ? `${titleColumnWidth}px` : undefined }}
                >
                    {displayOptions.showServiceTitle ? (
                        <div className="flex items-start gap-1 font-normal">
                            <TagIcon name={service.category} size='xxs' hideText />
                            <ServiceTitle service={service} hideProviders={true} links={false} />
                        </div>
                    ) : (
                        <div className="flex items-start gap-1.5 font-normal">
                            <div className='flex items-start gap-1 text-xs'>
                                <TagIcon name={service.category} size='xxs' hideText />
                                {ServiceTitleStatic(service)} <span className="text-slate-400 font-light">#{service.id}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Attributes & Observations */}
            <div className='flex-2 flex flex-wrap gap-1'>
                <ShowSelfAttrs
                    className="flex flex-wrap items-start justify-start gap-1"
                    attrs={booking.self_attrs}
                    context={booking}
                    size="xs"
                    include={displayOptions.selectedAttrKeys}
                    showkey={false}
                />
                {booking.observations && (
                    <div className="text-xs bg-red-50 border border-red-100 px-1 py-0.5 rounded-md break-words w-fit">
                        {booking.observations}
                    </div>
                )}
            </div>

            {/* Service Order */}
            {displayOptions.showServiceOrder && (
                <div className="shrink-0 flex justify-center">
                    <BookingServiceOrders booking={booking} editable={editable} />
                </div>
            )}

            {/* Provider */}
            {displayOptions.showProvider && (
                <div className="shrink-0">
                    <ProviderSelector
                        booking={booking}
                        size="xs"
                        editable={editable}
                    />
                </div>
            )}

            {/* Coordinator */}
            {displayOptions.showCoordinator && (
                <div className="shrink-0">
                    <CoordinatorSelector
                        booking={booking}
                        size="xs"
                        editable={editable}
                    />
                </div>
            )}


            {/* Cost */}
            {displayOptions.showCost && (
                <div className="font-bold text-red-700 flex items-baseline gap-0.5 justify-end">
                    <span className="font-light text-red-400 text-[10px]">$</span>
                    {formatPrice(booking.money_out_amount)}
                    <span className="text-[9px] text-gray-800 uppercase ml-0.5">{booking.money_out_currency}</span>
                </div>
            )}

            {/* Price */}
            {displayOptions.showPrice && (
                <div className="shrink-0 font-bold text-slate-700 flex items-baseline justify-end text-xs text-right">
                    {booking.parent_booking_id ? (
                        <div className="flex justify-end p-1">
                            <IconGift size={14} stroke={1.5} color="gray" />
                        </div>
                    ) : (
                        <>
                            <span className="font-light text-slate-400 text-[10px]">$</span>
                            {formatPrice(booking.money_in_amount)}
                            <span className="text-[9px] text-slate-800 uppercase ml-0.5">{booking.money_in_currency}</span>
                        </>
                    )}
                </div>
            )}
            {/* Booking ID */}
            {displayOptions.showBookingId && (
                <div className="px-2 py-1 rounded-md bg-gray-100 border border-gray-200 text-xxs font-bold text-gray-500">
                    #{booking.id}
                </div>
            )}
        </div>
    );
}

const getBookingExactServiceKey = (booking: ServiceBooking): string => {
    const serviceId = booking.service_id ?? booking.service_data?.id ?? 'none';
    const startDay = booking.start_day || '';
    const endDay = booking.end_day || '';
    const startHour = booking.start_hour || '';
    const endHour = booking.end_hour || '';
    const providerId = booking.selected_provider_id ?? booking.service_data?.providers?.[0]?.id ?? '';

    const sortedBookingAttrs = (booking.self_attrs || [])
        .map(a => `${a.key}:${a.value}`)
        .sort()
        .join(',');

    const sortedServiceAttrs = (booking.service_data?.self_attrs || [])
        .map(a => `${a.key}:${a.value}`)
        .sort()
        .join(',');

    return `${serviceId}|${startDay}|${endDay}|${startHour}|${endHour}|${providerId}|${sortedBookingAttrs}|${sortedServiceAttrs}`;
};

type BookingCluster =
    | { type: 'single'; booking: ServiceBooking }
    | { type: 'compressed_group'; key: string; bookings: ServiceBooking[] };

const buildClusters = (
    list: ServiceBooking[],
    compressIdentical: boolean
): BookingCluster[] => {
    if (!compressIdentical) {
        return list.map(b => ({
            type: 'single',
            booking: b,
        }));
    }

    const clustersList: BookingCluster[] = [];
    const groupMap = new Map<string, ServiceBooking[]>();
    const groupOrder: string[] = [];

    list.forEach(booking => {
        const key = getBookingExactServiceKey(booking);
        if (!groupMap.has(key)) {
            groupMap.set(key, []);
            groupOrder.push(key);
        }
        groupMap.get(key)!.push(booking);
    });

    groupOrder.forEach(key => {
        const items = groupMap.get(key)!;
        if (items.length > 4) {
            clustersList.push({
                type: 'compressed_group',
                key,
                bookings: items,
            });
        } else {
            items.forEach(b => {
                clustersList.push({
                    type: 'single',
                    booking: b,
                });
            });
        }
    });

    return clustersList;
};

interface BookingGroupRowProps {
    bookings: ServiceBooking[];
    reservation?: Reservation;
    displayOptions: any;
    editable: boolean;
    selectedIds: number[];
    onSelectRow?: (id: number) => void;
    paxColumnWidth?: number;
    titleColumnWidth?: number;
    isExpanded: boolean;
    onToggleExpand: () => void;
}

function BookingGridGroupRow({
    bookings,
    reservation,
    displayOptions,
    editable,
    selectedIds,
    onSelectRow,
    paxColumnWidth,
    titleColumnWidth,
    isExpanded,
    onToggleExpand,
}: BookingGroupRowProps) {
    const { t } = useTranslation();
    const firstBooking = bookings[0];
    if (!firstBooking) return null;

    const service = firstBooking.service_data;

    const totalPaxs = useMemo(() => {
        return bookings.reduce(
            (acc, b) => {
                acc.adults += b.adults || 0;
                acc.children += b.children || 0;
                acc.seniors += b.seniors || 0;
                acc.infants += b.infants || 0;
                acc.guests += b.guests || 0;
                return acc;
            },
            { adults: 0, children: 0, seniors: 0, infants: 0, guests: 0 }
        );
    }, [bookings]);

    const aggregatedPaxGroups = useMemo(() => {
        const groupMap = new Map<string | number, {
            key: string | number;
            name: string;
            personsMap: Map<string | number, Person>;
        }>();

        bookings.forEach((b) => {
            (b.pax_groups || []).forEach((bg) => {
                const paxGroup = bg.pax_group || reservation?.pax_groups?.find((rg) => rg.id === bg.pax_group_id);
                const groupKey = bg.pax_group_id || paxGroup?.id || bg.custom_name || paxGroup?.name || bg.id || 'default';
                const groupName = (bg.custom_name || paxGroup?.name || '').trim();

                if (!groupMap.has(groupKey)) {
                    groupMap.set(groupKey, {
                        key: groupKey,
                        name: groupName,
                        personsMap: new Map(),
                    });
                }

                const entry = groupMap.get(groupKey)!;
                if (!entry.name && groupName) {
                    entry.name = groupName;
                }

                const hasExplicitBookingPersons = Array.isArray(bg.booking_persons) && (bg.booking_persons.length > 0 || bg.is_custom_config);
                const persons: Person[] = hasExplicitBookingPersons
                    ? (bg.booking_persons || [])
                        .map((bp) => bp.person || bp.person_data || paxGroup?.persons?.find((gp) => gp.id === bp.person_id))
                        .filter((p): p is Person => Boolean(p))
                    : (paxGroup?.persons || []);

                persons.forEach((p) => {
                    const personKey = p.id || p.document_number || p.name;
                    if (personKey && !entry.personsMap.has(personKey)) {
                        entry.personsMap.set(personKey, p);
                    }
                });
            });
        });

        return Array.from(groupMap.values())
            .map((g) => ({
                key: g.key,
                name: g.name,
                persons: sortPersons(Array.from(g.personsMap.values())),
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [bookings, reservation?.pax_groups]);

    const totalCost = useMemo(() => {
        return bookings.reduce((sum, b) => sum + (b.money_out_amount || 0), 0);
    }, [bookings]);
    const costCurrency = firstBooking.money_out_currency || '';

    const totalPrice = useMemo(() => {
        return bookings.reduce((sum, b) => sum + (b.money_in_amount || 0), 0);
    }, [bookings]);
    const priceCurrency = firstBooking.money_in_currency || '';

    const ids = useMemo(() => {
        return bookings.map(b => b.id).filter((id): id is number => typeof id === 'number');
    }, [bookings]);

    const minId = ids.length > 0 ? Math.min(...ids) : null;
    const maxId = ids.length > 0 ? Math.max(...ids) : null;

    const allSelected = ids.length > 0 && ids.every(id => selectedIds.includes(id));
    const someSelected = ids.some(id => selectedIds.includes(id)) && !allSelected;

    const handleToggleAll = (e: React.MouseEvent | React.ChangeEvent) => {
        e.stopPropagation();
        if (!onSelectRow) return;
        if (allSelected) {
            ids.forEach(id => {
                if (selectedIds.includes(id)) {
                    onSelectRow(id);
                }
            });
        } else {
            ids.forEach(id => {
                if (!selectedIds.includes(id)) {
                    onSelectRow(id);
                }
            });
        }
    };

    return (
        <div className="border-b border-slate-300 last:border-b-0">
            <div
                onClick={onToggleExpand}
                className={`
                    text-xs cursor-pointer select-none
                    flex flex-wrap items-start justify-between gap-2.5
                    px-2.5 py-1.5
                    transition-colors duration-150
                    ${isExpanded
                        ? 'bg-green-50/80 border-b border-green-200'
                        : 'bg-slate-50/70 hover:bg-indigo-50/50'
                    }
                `}
            >
                {/* 1. Toggle/Action */}
                {onSelectRow && (
                    <div
                        className="shrink-0 text-center flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={handleToggleAll}
                        />
                    </div>
                )}

                {/* Dates */}
                <div className="shrink-0 w-[70px]" onClick={(e) => e.stopPropagation()}>
                    <ServiceDatePopover
                        icon={true}
                        size="xs"
                        serviceCategory={service?.category}
                        value={[firstBooking.start_day, firstBooking.end_day]}
                        onChange={() => { }}
                        disabled={true}
                    />
                </div>

                {/* Start Hour */}
                {displayOptions.showHour && (
                    <div className="shrink-0 w-[80px]" onClick={(e) => e.stopPropagation()}>
                        <SelectTimePopover
                            icon={true}
                            size="xs"
                            value={firstBooking.start_hour}
                            onChange={() => { }}
                            disabled={true}
                        />
                    </div>
                )}

                {/* Passengers / Pax Size */}
                {(displayOptions.groupByPaxs || displayOptions.showPaxs) && (
                    <div
                        className="shrink-0 flex flex-wrap items-center gap-1.5"
                        style={{ minWidth: paxColumnWidth ? `${paxColumnWidth}px` : undefined }}
                    >
                        <ShowPaxGroup
                            adults={totalPaxs.adults}
                            children={totalPaxs.children}
                            seniors={totalPaxs.seniors}
                            infants={totalPaxs.infants}
                            guests={totalPaxs.guests}
                        />
                        {!displayOptions.groupByPaxs && displayOptions.showPaxName && aggregatedPaxGroups.length > 0 && (
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] min-w-0">
                                {aggregatedPaxGroups.map((group) => (
                                    <div key={group.key} className="inline-flex flex-wrap items-center gap-1 min-w-0">
                                        {group.name && (
                                            <span className="inline-flex items-center gap-1 font-semibold text-slate-700">
                                                <IconUsersGroup size={11} className="shrink-0 text-indigo-500" />
                                                <span className="break-words">{group.name}</span>
                                            </span>
                                        )}
                                        {aggregatedPaxGroups.length === 1 && group.persons.map((p) => (
                                            <span
                                                key={p.id || p.document_number || p.name}
                                                className="inline-flex items-center gap-0.5 text-slate-600"
                                            >
                                                <IconUser size={10} className="shrink-0 text-slate-400" />
                                                <span className="break-words">{p.name}</span>
                                            </span>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Service Name */}
                {!displayOptions.hideServiceTitle && service && (
                    <div
                        className="shrink-0"
                        style={{ width: titleColumnWidth ? `${titleColumnWidth}px` : undefined }}
                    >
                        {displayOptions.showServiceTitle ? (
                            <div className="flex items-start gap-1 font-normal">
                                <TagIcon name={service.category} size="xxs" hideText />
                                <ServiceTitle service={service} hideProviders={true} links={false} />
                            </div>
                        ) : (
                            <div className="flex items-start gap-1.5 font-normal">
                                <div className="flex items-start gap-1 text-xs">
                                    <TagIcon name={service.category} size="xxs" hideText />
                                    {ServiceTitleStatic(service)} <span className="text-slate-400 font-light">#{service.id}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Count Badge & Attributes */}
                <div className="flex-2 flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xxs font-semibold bg-slate-200/80 text-slate-700 border border-slate-300/80 shrink-0">
                        <IconStack2 size={11} stroke={2} className="shrink-0 text-slate-600" />
                        <span>{bookings.length} {t('bookings')}</span>
                    </span>
                    <ShowSelfAttrs
                        className="flex flex-wrap items-start justify-start gap-1"
                        attrs={firstBooking.self_attrs}
                        context={firstBooking}
                        size="xs"
                        include={displayOptions.selectedAttrKeys}
                        showkey={false}
                    />
                </div>

                {/* Service Order */}
                {displayOptions.showServiceOrder && (
                    <div className="shrink-0 flex justify-center" onClick={(e) => e.stopPropagation()}>
                        <BookingServiceOrders booking={firstBooking} editable={false} />
                    </div>
                )}

                {/* Provider */}
                {displayOptions.showProvider && (
                    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                        <ProviderSelector
                            booking={firstBooking}
                            size="xs"
                            editable={false}
                        />
                    </div>
                )}

                {/* Coordinator */}
                {displayOptions.showCoordinator && (
                    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                        <CoordinatorSelector
                            booking={firstBooking}
                            size="xs"
                            editable={false}
                        />
                    </div>
                )}

                {/* Cost */}
                {displayOptions.showCost && (
                    <div className="font-bold text-red-700 flex items-baseline gap-0.5 justify-end">
                        <span className="font-light text-red-400 text-[10px]">$</span>
                        {formatPrice(totalCost)}
                        <span className="text-[9px] text-gray-800 uppercase ml-0.5">{costCurrency}</span>
                    </div>
                )}

                {/* Price */}
                {displayOptions.showPrice && (
                    <div className="shrink-0 font-bold text-slate-700 flex items-baseline justify-end text-xs text-right">
                        {firstBooking.parent_booking_id ? (
                            <div className="flex justify-end p-1">
                                <IconGift size={14} stroke={1.5} color="gray" />
                            </div>
                        ) : (
                            <>
                                <span className="font-light text-slate-400 text-[10px]">$</span>
                                {formatPrice(totalPrice)}
                                <span className="text-[9px] text-slate-800 uppercase ml-0.5">{priceCurrency}</span>
                            </>
                        )}
                    </div>
                )}

                {/* Booking ID Range + Chevron */}
                <div className="flex items-center gap-1 shrink-0">
                    {displayOptions.showBookingId && minId !== null && (
                        <Tooltip
                            label={ids.map(id => `#${id}`).join(', ')}
                            withArrow
                            multiline
                            disabled={ids.length <= 1}
                        >
                            <div className="px-2 py-1 rounded-md bg-gray-100 border border-gray-200 text-xxs font-bold text-gray-500">
                                {minId === maxId ? `#${minId}` : `#${minId} - #${maxId}`}
                            </div>
                        </Tooltip>
                    )}
                    <IconChevronDown
                        size={14}
                        stroke={2}
                        className={`text-slate-500 transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-180 text-indigo-600' : ''}`}
                    />
                </div>
            </div>

            {isExpanded && (
                <div className="ml-3 pl-2 border-l-2 border-indigo-200 divide-y divide-slate-200 bg-white">
                    {bookings.map((booking) => (
                        <BookingGridRow
                            key={booking.id}
                            booking={booking}
                            reservation={reservation}
                            displayOptions={displayOptions}
                            editable={editable}
                            isSelected={selectedIds.includes(booking.id)}
                            onSelectRow={onSelectRow}
                            paxColumnWidth={paxColumnWidth}
                            titleColumnWidth={titleColumnWidth}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

interface ReservationsListWitColumnOptionsProps {
    reservations: Reservation[];
    showFilters?: boolean;
    displayOptions: any;
    selectedIds?: number[]
    onSelectRow?: (id: number) => void;
}

export default function ReservationsList({
    reservations,
    displayOptions,
    selectedIds = [],
    onSelectRow,
}: ReservationsListWitColumnOptionsProps): JSX.Element {
    const { t } = useTranslation();

    const activeDisplayOptions = useMemo(() => ({
        showClientName: true,
        showProvider: false,
        showCoordinator: false,
        editable: false,
        groupByPaxs: true,
        compressIdentical: true,
        showPaxs: true,
        showPrice: true,
        showCost: true,
        showPaymentStatus: true,
        showExternalCode: true,
        ...displayOptions,
    }), [displayOptions]);

    const editable = activeDisplayOptions.editable || false;
    const compressIdentical = activeDisplayOptions.compressIdentical !== false;
    const [expandedGroupKeys, setExpandedGroupKeys] = useState<Record<string, boolean>>({});

    const processedReservations = useMemo(() => {
        return reservations
            .map(reservation => {
                const bookings = (reservation.service_bookings || []).filter(booking => {
                    if (activeDisplayOptions.hideServicePackage) {
                        return !(booking.service_data?.child_services?.length > 0);
                    }
                    return true;
                });

                // Group bookings by passengers if enabled
                const groupedByPax = activeDisplayOptions.groupByPaxs
                    ? (() => {
                        const paxsGroupsMap = new Map<string, {
                            key: string;
                            paxGroups: {
                                id?: number;
                                name: string;
                                adults: number;
                                children: number;
                                seniors: number;
                                infants: number;
                                guests: number;
                            }[];
                            paxs: (Person & { adults?: number; children?: number; seniors?: number; infants?: number; guests?: number })[];
                            bookings: ServiceBooking[];
                            adults: number;
                            children: number;
                            seniors: number;
                            infants: number;
                            guests: number;
                        }>();
                        bookings.forEach(booking => {
                            const bookingWithOriginal = booking as ServiceBooking & { _original_booking?: ServiceBooking };
                            const bookingGroups: ServiceBookingPaxGroup[] = (booking.pax_groups && booking.pax_groups.length > 0)
                                ? booking.pax_groups
                                : (bookingWithOriginal._original_booking?.pax_groups || []);

                            const groupPersons: Person[] = [];
                            const groupItems: {
                                id?: number;
                                name: string;
                                adults: number;
                                children: number;
                                seniors: number;
                                infants: number;
                                guests: number;
                            }[] = [];

                            bookingGroups.forEach((bg: ServiceBookingPaxGroup) => {
                                const pg = bg.pax_group;
                                const gName = (bg.custom_name || pg?.name || '').trim();
                                const gId = bg.pax_group_id || pg?.id;
                                if (gName && !groupItems.some(g => (gId && g.id === gId) || g.name === gName)) {
                                    groupItems.push({
                                        id: gId,
                                        name: gName,
                                        adults: bg.adults ?? pg?.adults ?? 0,
                                        children: bg.children ?? pg?.children ?? 0,
                                        seniors: bg.seniors ?? pg?.seniors ?? 0,
                                        infants: bg.infants ?? pg?.infants ?? 0,
                                        guests: bg.guests ?? pg?.guests ?? 0,
                                    });
                                }

                                const hasExplicitBookingPersons = Array.isArray(bg.booking_persons) && (bg.booking_persons.length > 0 || bg.is_custom_config);
                                if (hasExplicitBookingPersons) {
                                    (bg.booking_persons || []).forEach(bp => {
                                        const basePerson = bp.person || bp.person_data || pg?.persons?.find(gp => gp.id === bp.person_id);
                                        if (basePerson && !groupPersons.some(existing => existing.id === basePerson.id)) {
                                            groupPersons.push({
                                                ...basePerson,
                                                adults: bp.adults ?? basePerson.adults ?? 0,
                                                children: bp.children ?? basePerson.children ?? 0,
                                                seniors: bp.seniors ?? basePerson.seniors ?? 0,
                                                infants: bp.infants ?? basePerson.infants ?? 0,
                                                guests: bp.guests ?? basePerson.guests ?? 0,
                                            });
                                        }
                                    });
                                } else if (pg?.persons) {
                                    pg.persons.forEach((p: Person) => {
                                        if (!groupPersons.some(existing => existing.id === p.id)) {
                                            const sbp = p.ServiceBookingPerson;
                                            groupPersons.push({
                                                ...p,
                                                adults: sbp?.adults ?? p.adults ?? 0,
                                                children: sbp?.children ?? p.children ?? 0,
                                                seniors: sbp?.seniors ?? p.seniors ?? 0,
                                                infants: sbp?.infants ?? p.infants ?? 0,
                                                guests: sbp?.guests ?? p.guests ?? 0,
                                            });
                                        }
                                    });
                                }
                            });

                            const resolvedPersons = groupPersons;

                            const hasPersons = resolvedPersons.length > 0;
                            const sortedPaxs = hasPersons
                                ? [...resolvedPersons].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                : [];
                            const groupNamesStr = groupItems.map(g => g.name).filter(Boolean).join(', ');
                            const paxNamesStr = sortedPaxs.map(p => p.name).filter(Boolean).join(', ');
                            const keyParts = [groupNamesStr, paxNamesStr].filter(Boolean);
                            const key = keyParts.length > 0 ? keyParts.join(' :: ') : 'no_names';

                            if (!paxsGroupsMap.has(key)) {
                                paxsGroupsMap.set(key, {
                                    key,
                                    paxGroups: [],
                                    paxs: [],
                                    bookings: [],
                                    adults: 0,
                                    children: 0,
                                    seniors: 0,
                                    infants: 0,
                                    guests: 0,
                                });
                            }
                            const group = paxsGroupsMap.get(key)!;
                            group.bookings.push(booking);
                            group.adults += booking.adults || 0;
                            group.children += booking.children || 0;
                            group.seniors += booking.seniors || 0;
                            group.infants += booking.infants || 0;
                            group.guests += booking.guests || 0;

                            groupItems.forEach(gi => {
                                if (!group.paxGroups.some(existing => (gi.id && existing.id === gi.id) || existing.name === gi.name)) {
                                    group.paxGroups.push(gi);
                                }
                            });

                            if (sortedPaxs.length > 0) {
                                sortedPaxs.forEach(p => {
                                    if (!group.paxs.some(existing => existing.id === p.id)) {
                                        group.paxs.push({
                                            ...p,
                                            adults: p.adults ?? 0,
                                            children: p.children ?? 0,
                                            seniors: p.seniors ?? 0,
                                            infants: p.infants ?? 0,
                                            guests: p.guests ?? 0,
                                        });
                                    }
                                });
                            }
                        });
                        const groups = Array.from(paxsGroupsMap.values());
                        groups.forEach(group => {
                            group.bookings = sortServiceBookings(group.bookings);
                        });
                        return groups;
                    })()
                    : [];

                return {
                    reservation,
                    bookings: sortServiceBookings(bookings),
                    groupedByPax,
                };
            })
            .sort((a, b) => a.reservation.start_day.localeCompare(b.reservation.start_day)); //from earliest to latest
    }, [reservations, activeDisplayOptions.hideServicePackage, activeDisplayOptions.groupByPaxs]);

    return (
        <div className="space-y-4 print-container">
            {processedReservations.map(({ reservation, bookings, groupedByPax }) => {
                const { maxPaxWidth, maxTitleWidth } = computeReservationColumnWidths(bookings, activeDisplayOptions);

                return (
                    <div
                        key={reservation.id}
                        className="bg-white border border-slate-300 rounded-lg hover:shadow-xl"
                    >

                        {/* add toggle to show/hide body */}
                        <ReservationCardHeader
                            reservation={reservation}
                            activeDisplayOptions={activeDisplayOptions}
                        />

                        {/* Reservation Bookings Grid Body, TODO: show/hide toggle */}
                        <div className="p-2 divide-y divide-gray-300 flex-1 min-w-0">
                            {bookings.length > 0 ? (
                                <>
                                    {activeDisplayOptions.groupByPaxs ? (
                                        <div className="space-y-1">
                                            {groupedByPax.map((group, groupIdx) => (
                                                <div
                                                    key={group.key || groupIdx}
                                                    className="rounded-lg border border-gray-300 bg-gray-50/40 shadow-2xs overflow-hidden transition-colors duration-200"
                                                >
                                                    {/* Passenger Group Header */}
                                                    <div className="bg-slate-100/90 border-b border-slate-200 px-2 py-1 flex items-center justify-between gap-4 w-full overflow-hidden">
                                                        <div className="flex flex-wrap gap-2 items-center text-xs">

                                                            {group.key === 'no_names' && group.paxGroups.length === 0 && group.paxs.length === 0 ? (
                                                                <div className="flex items-center gap-1.5 font-bold text-indigo-950 bg-indigo-100/80 px-2 py-0.5 rounded border border-indigo-200/80 shadow-2xs text-xs">
                                                                    <IconUsersGroup size={13} className="shrink-0 text-indigo-600" stroke={1.8} />
                                                                    <span>{t('group')} {reservation.client_business?.name}</span>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    {group.paxGroups.map((pg, pgIdx) => {
                                                                        const hasGroupCounts = (pg.adults + pg.children + pg.seniors + pg.infants + pg.guests) > 0;
                                                                        return (
                                                                            <div
                                                                                key={pg.id || `${pg.name}-${pgIdx}`}
                                                                                className="flex items-center gap-1.5 font-bold text-indigo-950 bg-indigo-100/80 px-2 py-0.5 rounded border border-indigo-200/80 shadow-2xs text-xs"
                                                                            >
                                                                                {pg.name && (
                                                                                    <>
                                                                                        <IconUsersGroup size={13} className="shrink-0 text-indigo-600" stroke={1.8} />
                                                                                        <span>{pg.name}</span>
                                                                                    </>
                                                                                )}
                                                                                {hasGroupCounts && (
                                                                                    <ShowPaxGroup
                                                                                        size="xs"
                                                                                        adults={pg.adults}
                                                                                        children={pg.children}
                                                                                        seniors={pg.seniors}
                                                                                        infants={pg.infants}
                                                                                        guests={pg.guests}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    {group.paxs.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(p => {
                                                                        const hasPaxCounts = ((p.adults || 0) + (p.children || 0) + (p.seniors || 0) + (p.infants || 0) + (p.guests || 0)) > 0;
                                                                        return (
                                                                            <div key={p.id} className="flex flex-wrap items-center gap-2 bg-gray-200 px-2 py-0.5 rounded border border-slate-200/80 shadow-2xs text-xs">
                                                                                {p.name && (
                                                                                    <div className="flex items-center gap-1 font-bold">
                                                                                        <IconUser size={13} className="shrink-0 text-blue-600" stroke={1.5} />
                                                                                        <span>{p.name}</span>
                                                                                        {hasPaxCounts && (
                                                                                            <ShowPaxGroup
                                                                                                size="xs"
                                                                                                adults={p.adults}
                                                                                                children={p.children}
                                                                                                seniors={p.seniors}
                                                                                                infants={p.infants}
                                                                                                guests={p.guests}
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                                {p.phone && (
                                                                                    <div className="flex items-center gap-0.5 text-gray-800 font-normal">
                                                                                        <IconPhone size={11} className="shrink-0 text-slate-400" />
                                                                                        <span>{p.phone}</span>
                                                                                    </div>
                                                                                )}
                                                                                {p.document_number && (
                                                                                    <div className="flex items-center gap-0.5 text-gray-800 font-normal">
                                                                                        <IconId size={11} className="shrink-0 text-slate-400" />
                                                                                        <span>{p.document_number}</span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {/* Bookings inside passenger group */}
                                                    {buildClusters(sortServiceBookings(group.bookings), compressIdentical).map((cluster) => {
                                                        if (cluster.type === 'single') {
                                                            return (
                                                                <BookingGridRow
                                                                    key={cluster.booking.id}
                                                                    booking={cluster.booking}
                                                                    reservation={reservation}
                                                                    displayOptions={activeDisplayOptions}
                                                                    editable={editable}
                                                                    isSelected={selectedIds.includes(cluster.booking.id)}
                                                                    onSelectRow={onSelectRow}
                                                                    paxColumnWidth={maxPaxWidth}
                                                                    titleColumnWidth={maxTitleWidth}
                                                                />
                                                            );
                                                        }
                                                        const expandKey = `${reservation.id}-${group.key || groupIdx}-${cluster.key}`;
                                                        return (
                                                            <BookingGridGroupRow
                                                                key={expandKey}
                                                                bookings={cluster.bookings}
                                                                reservation={reservation}
                                                                displayOptions={activeDisplayOptions}
                                                                editable={editable}
                                                                selectedIds={selectedIds}
                                                                onSelectRow={onSelectRow}
                                                                paxColumnWidth={maxPaxWidth}
                                                                titleColumnWidth={maxTitleWidth}
                                                                isExpanded={Boolean(expandedGroupKeys[expandKey])}
                                                                onToggleExpand={() => {
                                                                    setExpandedGroupKeys(prev => ({
                                                                        ...prev,
                                                                        [expandKey]: !prev[expandKey],
                                                                    }));
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        buildClusters(bookings, compressIdentical).map((cluster) => {
                                            if (cluster.type === 'single') {
                                                return (
                                                    <BookingGridRow
                                                        key={cluster.booking.id}
                                                        booking={cluster.booking}
                                                        reservation={reservation}
                                                        displayOptions={activeDisplayOptions}
                                                        editable={editable}
                                                        isSelected={selectedIds.includes(cluster.booking.id)}
                                                        onSelectRow={onSelectRow}
                                                        paxColumnWidth={maxPaxWidth}
                                                        titleColumnWidth={maxTitleWidth}
                                                    />
                                                );
                                            }
                                            const expandKey = `${reservation.id}-${cluster.key}`;
                                            return (
                                                <BookingGridGroupRow
                                                    key={expandKey}
                                                    bookings={cluster.bookings}
                                                    reservation={reservation}
                                                    displayOptions={activeDisplayOptions}
                                                    editable={editable}
                                                    selectedIds={selectedIds}
                                                    onSelectRow={onSelectRow}
                                                    paxColumnWidth={maxPaxWidth}
                                                    titleColumnWidth={maxTitleWidth}
                                                    isExpanded={Boolean(expandedGroupKeys[expandKey])}
                                                    onToggleExpand={() => {
                                                        setExpandedGroupKeys(prev => ({
                                                            ...prev,
                                                            [expandKey]: !prev[expandKey],
                                                        }));
                                                    }}
                                                />
                                            );
                                        })
                                    )}
                                </>
                            ) : (
                                <div className="px-4 py-6 text-center text-slate-400 italic">
                                    {t('no_bookings')}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
